<template>
  <div class="table-header">
    <div class="table-header__columns">
      <div
        v-for="column in columns"
        :key="column.id"
        class="table-header__column"
        :class="`table-header__column--${column.id}`"
        role="button"
        tabindex="0"
        @click="handleSort(column.id)"
        @keydown.enter="handleSort(column.id)"
        @keydown.space.prevent="handleSort(column.id)"
      >
        <span class="table-header__column-title">{{ column.title }}</span>
        <v-icon
          v-if="column.sortable"
          size="16"
          :color="sortColumn === column.id ? '#000000' : '#00000061'"
          class="table-header__sort-icon"
        >
          {{ getSortIcon(column.id) }}
        </v-icon>
      </div>

      <div class="table-header__search-column">
        <v-text-field
          :model-value="searchQuery"
          placeholder=" Search Patients"
          variant="outlined"
          density="compact"
          hide-details
          class="table-header__search-input"
          @update:model-value="handleSearch"
        >
          <template #append-inner>
            <v-icon size="16" color="#795DE9">mdi-magnify</v-icon>
          </template>
        </v-text-field>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SortColumn, SortDirection } from "@virtual-care/shared";

interface Column {
  id: SortColumn;
  title: string;
  sortable: boolean;
}

interface Props {
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  searchQuery: string;
}

interface Emits {
  (e: "sort", column: SortColumn): void;
  (e: "search", query: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const columns: Column[] = [
  { id: "reason", title: "REASON", sortable: true },
  { id: "status", title: "STATUS", sortable: true },
  { id: "diagnosis", title: "PRIMARY DIAGNOSIS", sortable: true },
  { id: "room", title: "ROOM / BED / PATIENT", sortable: true },
];

const getSortIcon = (columnId: SortColumn): string => {
  if (props.sortColumn !== columnId) {
    return "mdi-sort";
  }

  if (props.sortDirection === "ASC") {
    return "mdi-sort-ascending";
  } else if (props.sortDirection === "DESC") {
    return "mdi-sort-descending";
  }

  return "mdi-sort";
};

const handleSort = (columnId: SortColumn) => {
  if (columnId) {
    emit("sort", columnId);
  }
};

const handleSearch = (value: string) => {
  emit("search", value);
};
</script>

<style lang="scss" scoped>
.table-header {
  background: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.table-header__columns {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 24px;
}

.table-header__column {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px 0 0;
  height: 34px;
  cursor: pointer;
  border-right: 1px solid #d4d1de;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &--reason {
    flex: 0 1 22%;
    min-width: 220px;
  }

  &--status {
    flex: 0 1 32%;
    min-width: 320px;
  }

  &--diagnosis {
    flex: 0 1 24%;
    min-width: 240px;
  }

  &--room {
    flex: 0 1 22%;
    min-width: 220px;
  }

  @media (max-width: 959px) {
    &--diagnosis {
      display: none;
    }
  }

  @media (max-width: 599px) {
    &--status,
    &--diagnosis {
      display: none;
    }
  }
}

.table-header__column-title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.333em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.6);
  flex: 1;
}

.table-header__sort-icon {
  flex-shrink: 0;
  margin-left: 4px;
}

.table-header__search-column {
  flex: 0 1 20%;
  min-width: 13rem;

  @media (max-width: 959px) {
    width: auto;
    flex: 1;
  }
}

.table-header__search-input {
  :deep(.v-field) {
    border-color: #795de9;
    border-radius: 8px;
    min-height: 34px;
    height: 34px;
    padding-right: 0 !important;
  }

  :deep(.v-field__input) {
    padding: 6px 8px;
    min-height: 34px;
    font-family: "Proxima Nova", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 1em;
  }

  :deep(.v-field__append-inner) {
    padding-right: 8px;
    align-items: center;
  }

  :deep(input::placeholder) {
    color: rgba(0, 0, 0, 0.6);
    opacity: 1;
  }
}

@media (max-width: 767px) {
  .table-header__columns {
    flex-wrap: wrap;
    padding: 12px 16px;
  }

  .table-header__search-column {
    width: 100%;
    order: -1;
    margin-bottom: 8px;
  }
}
</style>
